import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';
import { TiMetricsActionEventDetail, fetchWithTimeout } from '@ticom/web-component-utils';

@Component({
    tag: 'ti-portfolio-viewer',
    styleUrl: 'portfolio-viewer.scss',
    shadow: true
})
export class PortfolioViewer {

    private _svgWrapperElement: HTMLElement;

    /**
     * The element for this component.
     *
     * @type {HTMLElement}
     * @memberof PortfolioViewer
     */
    @Element() hostElement: HTMLElement;

    /**
     * svg image passed as a string
     *
     * @type {string}
     * @memberof PortfolioViewer
     */
    @State() svg: string;

    /**
     * Locale used for links.
     */
    @Prop() locale: string = 'en-US';

    /**
     * URL link for the svg image
     *
     * @type {string}
     * @memberof PortfolioViewer
     */
    @Prop() svgUrl: string;
    @Watch('svgUrl')
    handleSvgUrlChange(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue.length > 0) {
            this._updateData();
        }
    }

    /**
     * Event to call metrics function
     *
     * @type {EventEmitter<TiMetricsActionEventDetail>}
     * @memberof PortfolioViewer
     */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    componentWillLoad() {
        if (this.svgUrl) {
            this._updateData();
        }
    }

    componentDidRender() {
        if (!this._svgWrapperElement || !this._svgWrapperElement.childElementCount) {
            return;
        }

        // URL links for click and css for hover are specified in the shape data in Visio.
        // When exported to svg, it will have the following format:
        // <v:custProps>
        //      <v:cp v:nameU="url_link" v:lbl="url_link" v:type="0" v:langID="1033"
        // 		    v:val="VT4(https://www.ti.com/switches-multiplexers/analog/products.html#p2848typ=0.26;1&#38;#38;sort=p2848typ;asc)"/>
        // 	    <v:cp v:nameU="css_hover" v:lbl="css_hover" v:type="0" v:langID="1033"
        //          v:val="VT4(ti-portfolio-viewer-teal)"/>
        // </v:custProps>
        // The ':' character has special meaning inside a selector. You have to escape it.
        let custPropsTag = 'v\\:custProps';
        let custPropsList = this._svgWrapperElement.querySelectorAll(custPropsTag);
        // svg has '<v:custprops>' with lower-case 'p' sometimes, mainly in IE.
        if (!custPropsList || custPropsList.length <= 0) {
            custPropsTag = custPropsTag.toLowerCase();
            custPropsList = this._svgWrapperElement.querySelectorAll(custPropsTag);
        }
        custPropsList.forEach((custProps) => {
            const cpList = custProps.getElementsByTagName('v:cp');
            for (const cp of cpList) {
                const lbl = cp.getAttribute('v:lbl');
                // Get URL link for click and css style name for hover
                if (lbl === 'url_link' || lbl === 'css_hover') {
                    this._addClickAndCss(cp, custProps, lbl);
                }
            }
        });
    }

    private _addClickAndCss(cpElement: Element, custPropElement: Element, key: string) {
        let val = cpElement.getAttribute('v:val');
        // As the svg is generated in Visio, the v:val will have the format 'VT4({value}).
        // Extract the value in the parenthesis.
        const startIndex = val.indexOf('(') + 1;
        const endIndex = val.indexOf(')');
        val = val.slice(startIndex, endIndex);

        // Need to get the label for the block to pass to metrics function.
        // It is a child element <desc> of the group <g>
        // So, get the parent element <g> of <v:custProp>
        let parentEl: HTMLElement = custPropElement.parentElement;
        // IE cannot recognize parentElement, so use parentNode instead
        if (!parentEl) {
            parentEl = custPropElement.parentNode as HTMLElement;
        }
        if (parentEl) {
            let lbl = '';
            const descEls = parentEl.getElementsByTagName('desc');
            if (descEls && descEls.length > 0) {
                lbl = descEls[0].innerHTML;
                // IE doesn't support the innerHTML method on SVG Elements.
                if (!lbl) {
                    lbl = descEls[0].textContent;
                }
            }
            if (key === 'url_link') {
                parentEl.onclick = () => {
                    this._handleSvgClick(lbl, val);
                };
            } else if (key === 'css_hover') {
                const cssClasses: string[] = ['ti-portfolio-viewer-link'];
                cssClasses.push(val);
                parentEl.classList.add(...cssClasses);
            }
        }
    }

    private _handleSvgClick(name: string, url: string) {
        // 'https://www.ti.com' will be used only when url is relative
        const u = new URL(url, 'https://www.ti.com');

        // ignore external urls - process 'ti.com' or '*.ti.com'
        if (u.hostname.match(/(^|\.)ti\.com$/)) {
            // use current page location so that ti.com.cn and lower environments also work
            u.protocol = window.location.protocol;
            u.host = window.location.host;

            let regionPath = this.locale.toLowerCase();
            if (regionPath === 'en-us') {
                regionPath = '/';
            }
            else {
                regionPath = `/${regionPath}/`;
            }

            const path = u.pathname;
            // check path is to a '/product/' folder
            if (/^\/product\//.test(path)) {
                // product folder has special case for zh-cn
                if (regionPath === '/zh-cn/') {
                    regionPath = '/cn/';
                }
                u.pathname = path.replace(/^\/product\/(([a-z]{2}-[a-z]{2}|cn)\/)?/, `/product${regionPath}`);
            }
            // check path is to a products page
            else if (/\/products.html$/.test(path)) {
                u.pathname = path.replace(/^\/([a-z]{2}-[a-z]{2}\/)?/, regionPath);
            }
        }

        // metrics event - URL could be very long, so only send URL origin and pathname
        this.tiMetricsAction.emit({
            eventName: 'click',
            elementName: 'ti-portfolio-viewer',
            data: { value: name, url: `${u.origin}${u.pathname}` }
        });

        window.open(u.href, '_blank');
    }

    private _updateData() {
        // fetch svg from url
        fetchWithTimeout(this.svgUrl)
            .then(function (response) {
                if (response.ok) {
                    return response.text();
                }
                throw new Error(`Request for ${this.svgUrl} returned 404`);
            }).then((text) => {
                this.svg = text;
            }).catch(function () {
                // error
            });
    }

    render() {
        if (this.svg && this.svg.length) {
            return (
                <Host>
                    <div innerHTML={this.svg} ref={el => this._svgWrapperElement = el} class='ti-portfolio-viewer-svgContainer'></div>
                </Host>
            );
        }
    }

}
